/**
 * Single source of truth for every posture threshold in the product.
 * Rule (CLAUDE.md #7): no arbitrary numbers. Every value below carries its
 * source. New thresholds require a source comment — see docs/DATA_AND_ML.md §2.
 *
 * Bump THRESHOLDS_VERSION whenever a number changes; it is written to every
 * session document so past sessions stay interpretable (docs/DATA_AND_ML.md §7).
 */
export const THRESHOLDS_VERSION = 'v2';
/**
 * v1 → v2 (2026-10-31): the head metric moved from the ear–shoulder angle
 * (ESA) to an estimated CVA (C7→tragus) plus CHA. The two read on different
 * scales — ESA ran well above CVA — so v1 head numbers are never compared
 * with v2 ones; v1 sessions store them as `esa`, v2 as `cva`.
 */
/**
 * Minimum detectable change of photo-based craniovertebral angle: ~5°
 * (4.96–5.52°, Gallego-Izquierdo 2020). Everything the UI says about change
 * must respect this band: a week-over-week delta inside ±5° is "maintained".
 */
export const MDC_DEGREES = 5;
export const THRESHOLDS = {
    /**
     * CVA — craniovertebral angle: the line from C7 to the tragus against the
     * horizontal through C7. 90° = tragus straight above C7, smaller = head
     * further forward. MediaPipe has no C7 or tragus; both are estimated from
     * its landmarks by HEAD_NECK_MODEL below.
     *
     * Forward head below 50° (expert PT consultation, 2026-10-31; same line and
     * landmarks in Ruivo et al., Braz J Phys Ther 2014;18(4):364-71,
     * doi 10.1590/bjpt-rbf.2014.0027, after Diab & Moustafa and Yip et al.;
     * Yip's normal range 55.02 ± 2.86). Graded good ≥ 50°,
     * warn 45–50° (within one MDC of the line), bad < 45°.
     * Healthy adults average 48.76° ± 6.77 standing (Zárate-Tejero 2024,
     * DOI 10.3390/app14198639) and 47.4° ± 5.2 in adolescents (Ruivo 2014), so
     * many people sit under 50°: grade it, never phrase "< 50 = abnormal", and
     * apply the personal baseline rule below when a calibration exists
     * (DATA_AND_ML §2).
     */
    cva: {
        good: 50,
        warn: 45,
        /** Drop below the user's own upright baseline that counts as forward head, in degrees. */
        personalDropBad: 8,
    },
    /**
     * CHA — craniohorizontal angle: the tragus→outer-eye-corner line against the
     * horizontal, signed; positive = eye above the tragus (chin up). The head
     * tilt that comes with a forward head.
     * Above 20° counts as head tilted (expert PT consultation, 2026-10-31).
     * Context: the same tragus–lateral canthus angle averaged 17.2° ± 5.7 in 275
     * adolescents (Ruivo 2014), who give no cut-off. Looking down at a screen
     * (negative) is not graded.
     */
    cha: {
        max: 20,
    },
    /**
     * Trunk inclination — hip→shoulder vector vs. vertical; positive = leaning forward.
     * OSHA's computer-workstation eTool describes upright sitting (torso vertical,
     * thighs level: ~90° torso-thigh) and reclined sitting (105–120°) as neutral.
     * We take the upright-to-light-recline band, 90–110°, which maps to roughly
     * -5°…+10° of lean.
     */
    trunkInclination: {
        goodMin: -5,
        goodMax: 10,
        warnMax: 20,
        /** More negative than this = reclining away from the desk. */
        reclineBad: -10,
    },
    /**
     * Shoulder protraction — (x_shoulder − x_hip) / torsoLength, forward positive.
     * Geometric, not clinical: in slouch the shoulder travels ahead of the hip.
     * Cut-offs set from our own calibration fixtures; revisit after the Phase 5
     * marker sessions (DATA_AND_ML §2).
     */
    shoulderProtraction: {
        good: 0.1,
        warn: 0.2,
    },
    /** Hip angle — shoulder–hip–knee. 90–110°: OSHA eTool upright (~90°) to light recline (see trunkInclination). */
    hipAngle: {
        goodMin: 90,
        goodMax: 110,
        warnMin: 80,
        warnMax: 125,
    },
    /** Knee angle — hip–knee–ankle. Reference only; 90–120° recommended. */
    kneeAngle: {
        goodMin: 90,
        goodMax: 120,
        badBelow: 80,
    },
    /** Elbow angle — shoulder–elbow–wrist. Reference only (typing posture). */
    elbowAngle: {
        goodMin: 90,
        goodMax: 120,
    },
    /** Wrist deviation — elbow→wrist vs. wrist→index. Reference only. */
    wristDeviation: {
        good: 15,
        warn: 25,
    },
    /** Quality gate (docs/ARCHITECTURE.md §1.3). */
    gate: {
        /** |x(L shoulder) − x(R shoulder)| / shoulderHipLength below this = side view. */
        maxShoulderSeparationRatio: 0.25,
        earVisibility: 0.6,
        hipVisibility: 0.5,
        kneeVisibility: 0.5,
        /** Nose must sit below this normalized y (i.e. head room in frame). */
        minNoseY: 0.03,
        meanLumaMin: 40,
        meanLumaMax: 220,
        /** Seconds of continuous gate failure before the UI nags the user. */
        failureBannerSec: 30,
    },
};
/** Timing constants for the state machine and scoring (ARCHITECTURE §1.5). */
export const TIMING = {
    /** A non-good state must persist this long before it is committed (anti-flicker). */
    hysteresisSec: 3,
    /** A run of the same non-good state at least this long becomes an "episode". */
    episodeMinSec: 20,
    /**
     * Decay curve bucket width: 5 min since 2026-10-16 (15 before; checks are
     * now 15–30 min). Old sessions keep their 15-minute buckets.
     */
    decayBucketSec: 5 * 60,
    /** Window after an episode start in which returning to good counts as a recovery. */
    recoveryWindowSec: 60,
    /** Median filter width applied to metrics (posture is a slow signal). */
    medianWindowSec: 1,
    /** Max episodes stored on a session document. */
    maxEpisodesStored: 50,
};
export const SCORE_WEIGHTS = {
    goodRatio: 0.45,
    fhpFree: 0.3,
    slouchFree: 0.15,
    recovery: 0.1,
};
/** Default capture settings; Remote Config can override (ARCHITECTURE §2.7). */
export const CAPTURE_DEFAULTS = {
    sampleFps: 3,
    minSampleFps: 2,
    maxSampleFps: 5,
    poseModel: 'lite',
    thermalHintEveryMin: 30,
};
/**
 * Camera checks are 15 or 30 minutes (2026-10-16; up to 3 h before). The check
 * measures; breaks and exercise snacks happen in the day, outside the session.
 * 15 is the default (2026-10-17): a check people actually start beats a better
 * one they put off, and program checks are all 15 so weeks compare. 30 is
 * offered as the fuller look: it spans the 30–50 min break interval desk-work
 * guidance uses (breaks section of features/sources), so it shows whether
 * posture holds or drifts as sitting goes on.
 */
export const SESSION_LENGTH_CHOICES_MIN = [15, 30];
export const DEFAULT_SESSION_LENGTH_MIN = 15;
/** Higher-is-better metric (CVA): good ≥ good, warn ≥ warn, else bad. */
export function gradeDescending(value, good, warn) {
    if (value >= good)
        return 'good';
    if (value >= warn)
        return 'warn';
    return 'bad';
}
/** Lower-is-better metric (protraction): good ≤ good, warn ≤ warn, else bad. */
export function gradeAscending(value, good, warn) {
    if (value <= good)
        return 'good';
    if (value <= warn)
        return 'warn';
    return 'bad';
}
/** Two-sided metric (trunk, hip): inside good band, inside warn band, else bad. */
export function gradeBand(value, goodMin, goodMax, warnMin, warnMax) {
    if (value >= goodMin && value <= goodMax)
        return 'good';
    if (value >= warnMin && value <= warnMax)
        return 'warn';
    return 'bad';
}
/**
 * Forward-head cut-off for a user: the stricter of the published line
 * (CVA < 50°) and (personal upright baseline − personalDropBad).
 * ARCHITECTURE §1.4: "절대 임계값과 개인 상대 임계값 중 보수적인 쪽".
 */
export function effectiveCvaCutoff(baselineCva) {
    const absolute = THRESHOLDS.cva.good;
    if (baselineCva == null || !Number.isFinite(baselineCva))
        return absolute;
    const relative = baselineCva - THRESHOLDS.cva.personalDropBad;
    return Math.max(absolute, relative);
}
/**
 * Where C7 and the tragus sit relative to MediaPipe landmarks (none of the
 * 33 points is either). A geometric model, not a measurement: provisional
 * until the marker sessions (docs/CALIBRATION_SESSIONS.md) fit it.
 *
 * Tragus: just in front of the ear canal; MediaPipe's ear point sits on the
 * ear itself. Moved toward the outer eye corner by ≈1 cm of the ≈7.5 cm
 * ear-to-eye-corner span — our assumption, stated so it can be revisited.
 *
 * C7: above and behind the shoulder point, which we take as the acromion.
 *  - Height: C7 sits above the acromion by 0.137 × (acromion − trochanter
 *    height), pooled ANSUR II means (Gordon et al. 2014, 4,082 men 0.143,
 *    1,986 women 0.124), i.e. 0.137 torso lengths along the trunk line. With
 *    the hip hidden, 0.397 × the shoulder–ear distance (same data:
 *    C7−acromion over acromion−tragion height).
 *  - Depth: from the mean sagittal shoulder–C7 angle of 53.7° in 160
 *    asymptomatic adults (Raine & Twomey, Arch Phys Med Rehabil
 *    1997;78:1215-23): behind the acromion by height / tan(53.7°).
 *
 * Consequence: the model assumes average shoulders, so it cannot measure the
 * sagittal shoulder angle (SSP; acromion→C7 vs horizontal, protracted below
 * 52° — expert consultation 2026-10-31 and Ruivo 2014). SSP from this C7 would
 * read 53.7° plus the trunk lean for everyone. SSP needs a real C7
 * (DATA_AND_ML §2).
 */
export const HEAD_NECK_MODEL = {
    version: 'cva-est-v1',
    tragusTowardEye: 0.13,
    c7AbovePerTorso: 0.137,
    c7AbovePerEarDistance: 0.397,
    referenceSspDeg: 53.7,
};
/**
 * Standing side-on alignment for the posture check (DATA_AND_ML §2, "서 있을 때 정렬").
 *
 * Graded against measured healthy adults, NOT against Kendall's "ideal"
 * plumb line — that line is a convention that does not match healthy posture
 * or the true gravity line (Barra-López, J Rehabil Med 2024;56:jrm41899,
 * doi 10.2340/jrm.v56.41899).
 *
 * Reference: in healthy standing the ear sits ~5–6 cm in front of the ankle
 * (Woodhull et al., Eur J Appl Physiol 1985;54:109-15, doi 10.1007/BF00426309,
 * n=15: 5.9 ± 1.6 cm; Hasegawa et al., J Anat 2017;230:619-30,
 * doi 10.1111/joa.12586, n=136, EOS + force plate: ≈4.8 cm), i.e. the
 * ankle→ear line leans ≈2° forward. The ankle→shoulder line ("vertical
 * alignment of the body") measures 1.4–2.7° with SD 1.4–2.0° in photo studies
 * (Ferreira et al., Clinics 2010;65:675-81, doi 10.1590/S1807-59322010000700005).
 *
 * Bands are statistical, not clinical: aligned up to mean + 2 SD, "a little
 * forward" to mean + 3 SD, "forward" beyond (SD taken as 1.5°, the photo-based
 * spread). Converting Woodhull's centimetres to degrees assumes ~150 cm from
 * ankle to ear — our assumption, stated here so it can be revisited.
 *
 * Only angles from the camera-side landmarks are graded (Wade et al., PLoS One
 * 2023;18:e0293917: the far side carries about twice the error). Every angle
 * to vertical is off by however much the phone itself is tilted; the app
 * levels the landmarks by the accelerometer roll first (PHONE_TILT).
 */
export const STANDING_ALIGNMENT = {
    version: 'standing-v2',
    /**
     * Week-to-week change inside this is "no change". Provisional: about one
     * healthy SD; replace with MDC95 = 1.96·√2·SEM (Weir, J Strength Cond Res
     * 2005;19:231-40) from our own repeated checks. Habitual standing alone varies
     * by up to ~6° between sessions (Dunk et al., JMPT 2004;27:91-6).
     */
    noChangeDeg: 2,
    earLine: {
        /** Ankle→ear forward lean; healthy ≈2°. */
        leaningBackBelow: -1,
        alignedMax: 5,
        slightlyForwardMax: 6.5,
    },
    shoulderLine: {
        /** Ankle→shoulder forward lean; healthy 1.4–2.7°. */
        alignedMax: 5.5,
        slightlyForwardMax: 7,
    },
    measures: [
        {
            id: 'earLine',
            label: 'Head over feet',
            help: 'The line from your ankle to your ear, against vertical.',
        },
        {
            id: 'shoulderLine',
            label: 'Shoulders over feet',
            help: 'The line from your ankle to your shoulder, against vertical.',
        },
    ],
};
/** Grades for the standing step. Ear–hip offset is shown elsewhere but never graded (noisy hip landmark). */
export function gradeStanding(a) {
    const ear = STANDING_ALIGNMENT.earLine;
    const shoulder = STANDING_ALIGNMENT.shoulderLine;
    const earGrade = a.bodyLineDeg < ear.leaningBackBelow
        ? 'leaningBack'
        : a.bodyLineDeg <= ear.alignedMax
            ? 'aligned'
            : a.bodyLineDeg <= ear.slightlyForwardMax
                ? 'slightlyForward'
                : 'forward';
    const shoulderGrade = a.shoulderLineDeg <= shoulder.alignedMax
        ? 'aligned'
        : a.shoulderLineDeg <= shoulder.slightlyForwardMax
            ? 'slightlyForward'
            : 'forward';
    return { earLine: earGrade, shoulderLine: shoulderGrade };
}
/**
 * Body proportions for turning image ratios into centimetres when the user
 * gives their height. Segment heights as a fraction of stature H (Drillis &
 * Contini 1966, as tabulated in Winter DA, Biomechanics and Motor Control of
 * Human Movement, 4th ed., Wiley 2009, Fig. 4.1): eye level 0.936 H, ankle
 * 0.039 H. The ear sits at about eye level, so ear→ankle ≈ 0.897 H.
 */
export const ANTHROPOMETRY = {
    earToAnkleFraction: 0.936 - 0.039,
};
/**
 * Phone tilt in any capture. Engineering limits, not literature values.
 *
 * Roll (sideways, in the picture plane) is corrected: landmarks are rotated
 * level by the accelerometer reading before any angle is read, so a phone
 * propped at a slant still measures true vertical. The correction is trusted
 * up to `maxRollDeg`; past it the setup asks for a straighter phone.
 *
 * Pitch (leaning back against something, or tipping forward) cannot be
 * corrected without the lens geometry, which a web camera does not expose. It
 * mostly foreshortens vertical lengths, which leaves the side-on angles close;
 * it is recorded with every session and check, and limited to `maxPitchDeg`
 * so the body is not seen from well above or below.
 */
export const PHONE_TILT = {
    maxRollDeg: 10,
    maxPitchDeg: 25,
};
/**
 * When a phone is not keeping the fixed sample rate. An engineering threshold:
 * below 80% of the target for a full minute of recording, it is reported
 * (Crashlytics non-fatal + GA4 `capture_slow`, once per session) and the
 * session's `actualFps` shows it. The rate itself is never lowered.
 */
export const SLOW_CAPTURE = {
    belowFraction: 0.8,
    forSec: 60,
};
//# sourceMappingURL=thresholds.js.map